import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import { cn } from "@volca/ui/lib/utils";

// The inset sheet's edge is the overlay shadow's 1px ring alone: a border
// as well drew the edge twice.

type SheetSide = "top" | "right" | "bottom" | "left";
type SheetVariant = "default" | "inset";

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetBackdrop({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-blanket transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs",
        className,
      )}
      {...props}
    />
  );
}

function SheetViewport({
  className,
  side = "right",
  variant = "default",
  ...props
}: SheetPrimitive.Viewport.Props & {
  side?: SheetSide;
  variant?: SheetVariant;
}) {
  return (
    <SheetPrimitive.Viewport
      data-slot="sheet-viewport"
      className={cn(
        "fixed inset-0 z-50 grid",
        side === "bottom" && "grid-rows-[1fr_auto] pt-12",
        side === "top" && "grid-rows-[auto_1fr] pb-12",
        side === "left" && "flex justify-start",
        side === "right" && "flex justify-end",
        variant === "inset" && "sm:p-3",
        className,
      )}
      {...props}
    />
  );
}

/**
 * `backdrop={false}` drops the dimmed layer, for a sheet the page behind
 * stays usable with (pair it with `modal={false}` on `Sheet`).
 */
function SheetContent({
  className,
  children,
  side = "right",
  variant = "default",
  backdrop = true,
  showCloseButton = true,
  closeProps,
  portalProps,
  viewportClassName,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  variant?: SheetVariant;
  backdrop?: boolean;
  showCloseButton?: boolean;
  closeProps?: SheetPrimitive.Close.Props;
  portalProps?: SheetPrimitive.Portal.Props;
  viewportClassName?: string;
}) {
  const popup = (
    <SheetPrimitive.Popup
      data-slot="sheet-content"
      data-side={side}
      data-variant={variant}
      className={cn(
        variant === "default"
          ? "fixed z-50 flex flex-col gap-4 bg-surface-overlay bg-clip-padding text-sm text-ink shadow-overlay transition duration-200 ease-in-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem] data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm"
          : "pointer-events-auto relative flex max-h-full min-h-0 w-full min-w-0 flex-col bg-surface-overlay bg-clip-padding text-sm text-ink shadow-overlay transition-[opacity,translate] duration-200 ease-out data-ending-style:opacity-0 data-ending-style:duration-150 data-starting-style:opacity-0 sm:rounded-2xl",
        variant === "inset" && {
          "row-start-2 motion-safe:data-ending-style:translate-y-8 motion-safe:data-starting-style:translate-y-8": side === "bottom",
          "motion-safe:data-ending-style:-translate-y-8 motion-safe:data-starting-style:-translate-y-8": side === "top",
          "motion-safe:data-ending-style:-translate-x-8 motion-safe:data-starting-style:-translate-x-8": side === "left",
          "motion-safe:data-ending-style:translate-x-8 motion-safe:data-starting-style:translate-x-8": side === "right",
          "w-[calc(100%-(--spacing(12)))] max-w-md": side === "left" || side === "right",
        },
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <SheetPrimitive.Close
          data-slot="sheet-close"
          aria-label="Close"
          className={cn("absolute top-3", variant === "default" ? "right-3" : "end-3")}
          render={<Button variant="ghost" size="icon" />}
          {...closeProps}
        >
          <XIcon aria-hidden />
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Popup>
  );
  return (
    <SheetPortal {...portalProps}>
      {backdrop && <SheetBackdrop className={variant === "default" ? "duration-150" : undefined} />}
      {variant === "inset" ? (
        <SheetViewport
          side={side}
          variant={variant}
          className={cn(!backdrop && "pointer-events-none", viewportClassName)}
        >
          {popup}
        </SheetViewport>
      ) : popup}
    </SheetPortal>
  );
}

function SheetHeader({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn("flex flex-col gap-0.5 p-4", className),
        "data-slot": "sheet-header",
      } as React.ComponentProps<"div">,
      props,
    ),
    render,
  });
}

function SheetPanel({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain p-4",
          className,
        ),
        "data-slot": "sheet-panel",
      } as React.ComponentProps<"div">,
      props,
    ),
    render,
  });
}

function SheetFooter({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn("mt-auto flex flex-col gap-2 p-4", className),
        "data-slot": "sheet-footer",
      } as React.ComponentProps<"div">,
      props,
    ),
    render,
  });
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "font-heading text-base leading-[1.3] font-medium text-ink",
        className,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm leading-[1.5] text-subtle", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetBackdrop,
  SheetViewport,
  SheetHeader,
  SheetPanel,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
